import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getException } from "lib/helpers/errorHelper";
import { surveyService } from "services/SurveyService";
import {
  Places,
  Answers,
  AnswerResponse,
  DataTriger,
  SelectItem,
  SurveyToken,
  SurveyContent,
  SurveySection,
  PersonSurveySend,
  SurveyUserFormModel,
} from "types";

const name: string = "survey.action";

interface surveyState {
  surveyError: boolean;
  cities: Places[] | [];
  states: Places[] | [];
  surveyLoading: boolean;
  surveySeverity: string;
  answers: Answers[];
  surveyShowToast: boolean;
  surveyResponse: any;
  loadingAnswers: boolean;
  isTokenExpired: boolean;
  saveAnswersResponse: any;
  authToken: SurveyToken | null;
  surveyToken: SurveyToken | null;
  identificationTypes: SelectItem[];
  surveyContent: SurveyContent[] | [];
  surveyLoadingContent: boolean;
  surveySection: SurveySection | null;
  surveyPerson: SurveyUserFormModel | null;
}

const initialState: surveyState = {
  states: [],
  cities: [],
  answers: [],
  surveyError: false,
  surveySeverity: "",
  surveyLoading: false,
  authToken: null,
  surveyShowToast: false,
  surveyToken: null,
  surveyContent: [],
  surveyPerson: null,
  surveyResponse: null,
  loadingAnswers: false,
  isTokenExpired: false,
  identificationTypes: [],
  saveAnswersResponse: null,
  surveyLoadingContent: false,
  surveySection: null,
};

export const sendEmailSurvey = createAsyncThunk(
  `${name}/sendeMailSurvey`,
  async (personSurvey: PersonSurveySend, { rejectWithValue }) => {
    try {
      const response = await surveyService.sendeMailSurvey(personSurvey);
      return response;
    } catch ({ data }) {
      return rejectWithValue(getException(data));
    }
  }
);

export const sendSMSSurvey = createAsyncThunk(
  `${name}/sendSMSSurvey`,
  async (personSurvey: PersonSurveySend, { rejectWithValue }) => {
    try {
      const response = await surveyService.sendSMSSurvey(personSurvey);
      return response;
    } catch ({ data }) {
      return rejectWithValue(getException(data));
    }
  }
);

export const getIdentificationTypes = createAsyncThunk(`${name}/getDocumentTypes`, async () => {
  const response = await surveyService.getIdentificationTypes();
  return response;
});

export const saveSurveyPerson = createAsyncThunk(
  `${name}/SaveSurveyPerson`,
  async ({ person, sectionId }: AnswerResponse, { rejectWithValue }) => {
    try {
      const response = await surveyService.saveSurveyPerson({ person, sectionId });
      return response;
    } catch ({ data }) {
      return rejectWithValue(getException(data));
    }
  }
);

export const savePerson = createAsyncThunk(
  `${name}/savePerson`,
  async (person: SurveyUserFormModel, { rejectWithValue }) => {
    try {
      const response = await surveyService.savePerson(person);
      return response;
    } catch ({ data }) {
      return rejectWithValue(getException(data));
    }
  }
);

export const getSurveySection = createAsyncThunk<SurveySection, number>(
  `${name}/GetSurveySection`,
  async (sectionId: number, { rejectWithValue }) => {
    try {
      const response = await surveyService.getSurveySection(sectionId);
      return response;
    } catch ({ data }) {
      return rejectWithValue(getException(data));
    }
  }
);

export const getAuthTokenId = createAsyncThunk<SurveyToken, string>(
  `${name}/getAuthTokenId`,
  async (surveyTokenId: string, { rejectWithValue }) => {
    try {
      const response = await surveyService.authTokenId(surveyTokenId);
      return response;
    } catch ({ data }) {
      return rejectWithValue(getException(data));
    }
  }
);

export const getSurveyTokenId = createAsyncThunk<SurveyToken, string>(
  `${name}/GetSurveyTokenId`,
  async (identificationNumber: string, { rejectWithValue }) => {
    try {
      const response = await surveyService.getSurveyTokenId(identificationNumber);
      return response;
    } catch ({ data }) {
      return rejectWithValue(getException(data));
    }
  }
);

export const getSurveyContent = createAsyncThunk<SurveyContent[], string>(
  `${name}/GetContent`,
  async (tokenId: string, { rejectWithValue }) => {
    try {
      const response = await surveyService.getSurveyContent(tokenId);
      return response;
    } catch ({ data }) {
      return rejectWithValue(getException(data));
    }
  }
);

export const getAnswers = createAsyncThunk<Answers[], SurveyToken>(
  `${name}/GetAnswers`,
  async ({ personId, surveyTokenId }: SurveyToken, { rejectWithValue }) => {
    try {
      const response = await surveyService.getAnswers(personId, surveyTokenId);
      return response;
    } catch ({ data }) {
      return rejectWithValue(getException(data));
    }
  }
);

export const getStates = createAsyncThunk<Places[], DataTriger>(
  `${name}/getStates`,
  async ({ triggerId, searchId }: DataTriger, { rejectWithValue }) => {
    try {
      const response = await surveyService.getDataTrigger(triggerId, searchId);
      return response;
    } catch ({ data }) {
      return rejectWithValue(getException(data));
    }
  }
);

export const getCities = createAsyncThunk<Places[], DataTriger>(
  `${name}/getCities`,
  async ({ triggerId, searchId }: DataTriger, { rejectWithValue }) => {
    try {
      const response = await surveyService.getDataTrigger(triggerId, searchId);
      return response;
    } catch ({ data }) {
      return rejectWithValue(getException(data));
    }
  }
);

export const surveySlice = createSlice({
  name,
  initialState,
  extraReducers: (builder) => {
    /* SEND SURVEY BY EMAIL */
    builder.addCase(sendEmailSurvey.pending, (state) => {
      state.surveyLoading = true;
    });

    builder.addCase(sendEmailSurvey.fulfilled, (state, { payload }) => {
      state.surveyError = false;
      state.surveyLoading = false;
      state.surveyShowToast = true;
      state.surveySeverity = "success";
      state.surveyResponse = payload;
    });

    builder.addCase(sendEmailSurvey.rejected, (state, { payload }) => {
      state.surveyError = true;
      state.surveyLoading = false;
      state.surveyShowToast = true;
      state.surveySeverity = "error";
      state.surveyResponse = payload;
    });
    /* SEND SURVEY BY SMS */
    builder.addCase(sendSMSSurvey.pending, (state) => {
      state.surveyLoading = true;
    });

    builder.addCase(sendSMSSurvey.fulfilled, (state, { payload }) => {
      state.surveyError = false;
      state.surveyLoading = false;
      state.surveyShowToast = true;
      state.surveySeverity = "success";
      state.surveyResponse = payload;
    });

    builder.addCase(sendSMSSurvey.rejected, (state, { payload }) => {
      state.surveyError = true;
      state.surveyLoading = false;
      state.surveyShowToast = true;
      state.surveySeverity = "error";
      state.surveyResponse = payload;
    });
    /* getDocumentTypes */
    builder.addCase(getIdentificationTypes.pending, (state) => {
      state.surveyLoading = true;
    });
    builder.addCase(getIdentificationTypes.fulfilled, (state, { payload }) => {
      state.surveyLoading = false;
      state.identificationTypes = payload.map((p) => {
        const item: SelectItem = {
          text: p.descripcion,
          value: p.value,
        };
        return item;
      });
    });
    builder.addCase(getIdentificationTypes.rejected, (state, { payload }) => {
      state.surveyError = true;
      state.surveyLoading = false;
      state.surveySeverity = "error";
      state.surveyResponse = payload;
    });
    /* save Person */
    builder.addCase(savePerson.pending, (state) => {
      state.surveyLoading = true;
    });
    builder.addCase(savePerson.fulfilled, (state, { payload }) => {
      state.surveyError = false;
      state.surveyLoading = false;
      state.surveySeverity = "success";
      state.surveyResponse = payload;
    });
    builder.addCase(savePerson.rejected, (state, { payload }) => {
      state.surveyError = true;
      state.surveyLoading = false;
      state.surveySeverity = "error";
      state.surveyResponse = payload;
    });

    /** Get auth token by survey token id */
    builder.addCase(getAuthTokenId.pending, (state) => {
      state.surveyLoading = true;
    });
    builder.addCase(getAuthTokenId.fulfilled, (state, { payload }) => {
      state.surveyLoading = false;
      state.authToken = payload;
      state.isTokenExpired = false;
    });
    builder.addCase(getAuthTokenId.rejected, (state, { payload }) => {
      state.surveyError = true;
      state.surveyLoading = false;
      state.surveyShowToast = true;
      state.surveySeverity = "error";
      state.surveyResponse = payload;
      state.isTokenExpired = true;
    });
    /** Get survey token*/
    builder.addCase(getSurveyTokenId.pending, (state) => {
      state.surveyLoading = true;
    });
    builder.addCase(getSurveyTokenId.fulfilled, (state, { payload }) => {
      state.surveyLoading = false;
      state.surveyToken = payload;
    });
    builder.addCase(getSurveyTokenId.rejected, (state, { payload }) => {
      state.surveyError = true;
      state.surveyLoading = false;
      state.surveyShowToast = true;
      state.surveySeverity = "error";
      state.surveyResponse = payload;
    });
    /** Get survey content*/
    builder.addCase(getSurveyContent.pending, (state) => {
      state.surveyLoadingContent = true;
    });
    builder.addCase(getSurveyContent.fulfilled, (state, { payload }) => {
      state.surveyLoadingContent = false;
      state.surveyContent = payload;
    });
    builder.addCase(getSurveyContent.rejected, (state, { payload }) => {
      state.surveyLoadingContent = false;
    });
    /** Get Answers*/
    builder.addCase(getAnswers.pending, (state) => {
      state.surveyLoading = true;
    });
    builder.addCase(getAnswers.fulfilled, (state, { payload }) => {
      state.surveyLoading = false;
      state.answers = payload;
    });
    builder.addCase(getAnswers.rejected, (state, { payload }) => {
      state.surveyError = true;
      state.surveyLoading = false;
      state.surveyShowToast = true;
      state.surveySeverity = "error";
      state.surveyResponse = payload;
    });
    /** Get States with Data Trigger API*/
    builder.addCase(getStates.pending, (state) => {
      state.surveyLoading = true;
    });
    builder.addCase(getStates.fulfilled, (state, { payload }) => {
      state.surveyLoading = false;
      state.states = payload;
    });
    builder.addCase(getStates.rejected, (state, { payload }) => {
      state.surveyError = true;
      state.surveyLoading = false;
      state.surveyShowToast = true;
      state.surveySeverity = "error";
      state.surveyResponse = payload;
    });
    /** Get Cities with Data Trigger API*/
    builder.addCase(getCities.pending, (state) => {
      state.surveyLoading = true;
    });
    builder.addCase(getCities.fulfilled, (state, { payload }) => {
      state.surveyLoading = false;
      state.cities = payload;
    });
    builder.addCase(getCities.rejected, (state, { payload }) => {
      state.surveyError = true;
      state.surveyLoading = false;
      state.surveyShowToast = true;
      state.surveySeverity = "error";
      state.surveyResponse = payload;
    });
    /** Get survey section by id */
    builder.addCase(getSurveySection.pending, (state) => {
      state.loadingAnswers = true;
      state.surveySection = null;
    });
    builder.addCase(getSurveySection.fulfilled, (state, { payload }) => {
      state.loadingAnswers = false;
      state.surveySection = payload;
    });
    builder.addCase(getSurveySection.rejected, (state, { payload }) => {
      state.surveyError = true;
      state.loadingAnswers = false;
      state.surveyShowToast = true;
      state.surveySeverity = "error";
      state.surveyResponse = payload;
    });
    /* SAVE USERS ANSWERS */
    builder.addCase(saveSurveyPerson.pending, (state) => {
      state.surveyLoading = true;
    });
    builder.addCase(saveSurveyPerson.fulfilled, (state, { payload }) => {
      state.surveyLoading = false;
      state.surveyShowToast = true;
      state.surveyResponse = payload;
      state.surveySeverity = "success";
    });
    builder.addCase(saveSurveyPerson.rejected, (state, { payload }) => {
      state.surveyError = true;
      state.surveyLoading = false;
      state.surveyShowToast = true;
      state.surveySeverity = "error";
      state.surveyResponse = payload;
    });
  },
  reducers: {
    setError: (state, { payload }) => {
      state.surveyError = true;
      state.surveyShowToast = true;
      state.surveyResponse = payload?.message || payload;
      state.surveySeverity = payload.severity || "error";
    },
    setSuccess: (state, { payload }) => {
      state.surveyError = false;
      state.surveyShowToast = true;
      state.surveyResponse = payload;
      state.surveySeverity = "success";
    },
    hideSurveyToast: (state) => {
      state.surveyShowToast = false;
    },
    cleanStatesAndCities: (state) => {
      state.states = [];
      state.cities = [];
    },
    cleanCities: (state) => {
      state.cities = [];
    },
    clearSurveyResponse: (state) => {
      state.surveyResponse = null;
    },
    clearSurveyError: (state) => {
      state.surveyError = false;
    },
  },
});
export const {
  setError,
  setSuccess,
  cleanCities,
  hideSurveyToast,
  clearSurveyError,
  clearSurveyResponse,
  cleanStatesAndCities,
} = surveySlice.actions;

export default surveySlice.reducer;
